/**
 * @module CanvasFilterTree
 * @purpose Renders the facet filter sidebar with checkboxes built from live facets.
 * @depends AnalysisFacets, AnalysisFilters (types/analysis-artefacts)
 * @dependedOnBy CanvasView
 *
 * @rules
 * - Performance: tree built from props at render time — no memoisation needed at this size
 * - State: expandedNodes managed locally (which categories are collapsed/expanded)
 * - Errors: renders empty tree when facets is null
 */

import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { AnalysisFacets, AnalysisFilters, FacetEntry, IpRangeId, TimeBucketId, ArtefactType } from '../../../types/analysis-artefacts'

const FILTER_TREE_WIDTH = '250px'
const FIRST_CATEGORY_ID = 'artefact-type'

interface FilterCategory {
  id: string
  label: string
  entries: FacetEntry[]
}

interface TreeNodeRowProps {
  category: FilterCategory
  isExpanded: boolean
  onToggleExpand: (categoryId: string) => void
  selectedEntryIds: Set<string>
  onToggleEntry: (categoryId: string, entryId: string) => void
}

function TreeNodeRow({
  category,
  isExpanded,
  onToggleExpand,
  selectedEntryIds,
  onToggleEntry,
}: TreeNodeRowProps): React.ReactElement {
  return (
    <div>
      <button
        onClick={() => { onToggleExpand(category.id) }}
        className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm transition-colors hover:bg-[var(--accent-dim)]"
        style={{ color: 'var(--text)' }}
        aria-expanded={isExpanded}
      >
        {isExpanded
          ? <ChevronDown className="h-3 w-3 shrink-0" style={{ color: 'var(--text-muted)' }} />
          : <ChevronRight className="h-3 w-3 shrink-0" style={{ color: 'var(--text-muted)' }} />
        }
        <span className="flex-1 truncate text-xs font-[var(--font-mono)] tracking-wide uppercase">
          {category.label}
        </span>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {category.entries.reduce((sum, entry) => sum + entry.count, 0)}
        </span>
      </button>
      {isExpanded && (
        <div className="ml-4">
          {category.entries.map((entry) => (
            <FilterEntryRow
              key={entry.id}
              entry={entry}
              categoryId={category.id}
              isChecked={selectedEntryIds.has(entry.id)}
              onToggle={onToggleEntry}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface FilterEntryRowProps {
  entry: FacetEntry
  categoryId: string
  isChecked: boolean
  onToggle: (categoryId: string, entryId: string) => void
}

function FilterEntryRow({ entry, categoryId, isChecked, onToggle }: FilterEntryRowProps): React.ReactElement {
  return (
    <button
      onClick={() => { onToggle(categoryId, entry.id) }}
      className="flex w-full items-center gap-2 rounded px-2 py-1 text-left transition-colors hover:bg-[var(--accent-dim)]"
      style={{ color: 'var(--text)' }}
    >
      <input
        type="checkbox"
        checked={isChecked}
        onChange={() => { onToggle(categoryId, entry.id) }}
        onClick={(ev) => { ev.stopPropagation() }}
        className="h-3 w-3 shrink-0 accent-[var(--accent)]"
        aria-label={entry.label}
      />
      <span className="flex-1 truncate text-xs font-[var(--font-mono)] tracking-wide">
        {entry.label}
      </span>
      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
        {entry.count}
      </span>
    </button>
  )
}

interface CanvasFilterTreeProps {
  facets: AnalysisFacets | null
  selectedFilters: AnalysisFilters
  onFilterChange: (filters: AnalysisFilters) => void
  onClearAll: () => void
}

function buildCategories(facets: AnalysisFacets | null, labels: Record<string, string>): FilterCategory[] {
  if (!facets) return []
  const categories: FilterCategory[] = [
    { id: 'artefact-type', label: labels['artefactType'] ?? 'Artefact Type', entries: facets.artefactTypes },
    { id: 'endpoint', label: labels['endpoint'] ?? 'Endpoint', entries: facets.endpoints },
    { id: 'user', label: labels['user'] ?? 'User', entries: facets.usernames },
    { id: 'time-period', label: labels['timePeriod'] ?? 'Time Period', entries: facets.timeBuckets },
  ]
  if (facets.ipRanges !== null) {
    categories.push({ id: 'ip-range', label: labels['ipRange'] ?? 'IP Range', entries: facets.ipRanges })
  }
  return categories
}

function toggleStringArray(prev: string[], entryId: string): string[] {
  return prev.includes(entryId) ? prev.filter((id) => id !== entryId) : [...prev, entryId]
}

function withArtefactType(current: AnalysisFilters, next: string[]): AnalysisFilters {
  if (next.length === 0) {
    const { artefactType: _removed, ...rest } = current
    return rest
  }
  return { ...current, artefactType: next as ArtefactType[] }
}

function withEndpointId(current: AnalysisFilters, next: string[]): AnalysisFilters {
  if (next.length === 0) {
    const { endpointId: _removed, ...rest } = current
    return rest
  }
  return { ...current, endpointId: next }
}

function withUsername(current: AnalysisFilters, next: string[]): AnalysisFilters {
  if (next.length === 0) {
    const { username: _removed, ...rest } = current
    return rest
  }
  return { ...current, username: next }
}

function withTimeBucket(current: AnalysisFilters, next: string[]): AnalysisFilters {
  if (next.length === 0) {
    const { timeBucket: _removed, ...rest } = current
    return rest
  }
  return { ...current, timeBucket: next as TimeBucketId[] }
}

function withIpRange(current: AnalysisFilters, next: string[]): AnalysisFilters {
  if (next.length === 0) {
    const { ipRange: _removed, ...rest } = current
    return rest
  }
  return { ...current, ipRange: next as IpRangeId[] }
}

type CategoryToggler = (current: AnalysisFilters, entryId: string) => AnalysisFilters

const CATEGORY_TOGGLERS: Record<string, CategoryToggler> = {
  'artefact-type': (filters, id) => withArtefactType(filters, toggleStringArray(filters.artefactType ?? [], id)),
  'endpoint': (filters, id) => withEndpointId(filters, toggleStringArray(filters.endpointId ?? [], id)),
  'user': (filters, id) => withUsername(filters, toggleStringArray(filters.username ?? [], id)),
  'time-period': (filters, id) => withTimeBucket(filters, toggleStringArray(filters.timeBucket ?? [], id)),
  'ip-range': (filters, id) => withIpRange(filters, toggleStringArray(filters.ipRange ?? [], id)),
}

function applyEntryToggle(
  categoryId: string,
  entryId: string,
  current: AnalysisFilters,
): AnalysisFilters {
  const toggler = CATEGORY_TOGGLERS[categoryId]
  return toggler ? toggler(current, entryId) : current
}

function buildSelectedEntryIds(filters: AnalysisFilters): Set<string> {
  const ids = new Set<string>()
  filters.artefactType?.forEach((id) => { ids.add(id) })
  filters.endpointId?.forEach((id) => { ids.add(id) })
  filters.username?.forEach((id) => { ids.add(id) })
  filters.timeBucket?.forEach((id) => { ids.add(id) })
  filters.ipRange?.forEach((id) => { ids.add(id) })
  return ids
}

/**
 * Facet-driven filter tree sidebar.
 *
 * @behaviour Builds categories from live facets prop. Each leaf toggles the relevant
 *            AnalysisFilters field via onFilterChange. IP Range category omitted when
 *            facets.ipRanges is null (no netstat data available).
 * @errors Renders empty tree when facets is null.
 */
export function CanvasFilterTree({
  facets,
  selectedFilters,
  onFilterChange,
  onClearAll,
}: CanvasFilterTreeProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const categoryLabels = {
    artefactType: t('canvas.filters.artefactType'),
    endpoint: t('canvas.filters.endpoint'),
    user: t('canvas.filters.user'),
    timePeriod: t('canvas.filters.timePeriod'),
    ipRange: t('canvas.filters.ipRange'),
  }

  const categories = useMemo(
    () => buildCategories(facets, categoryLabels),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [facets, t],
  )

  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(
    () => new Set([FIRST_CATEGORY_ID])
  )

  const selectedEntryIds = useMemo(() => buildSelectedEntryIds(selectedFilters), [selectedFilters])

  const handleToggleExpand = useCallback((categoryId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev)
      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }
      return next
    })
  }, [])

  const handleToggleEntry = useCallback((categoryId: string, entryId: string) => {
    onFilterChange(applyEntryToggle(categoryId, entryId, selectedFilters))
  }, [onFilterChange, selectedFilters])

  return (
    <div
      className="flex h-full flex-col gap-2 overflow-y-auto p-2"
      style={{ borderRight: '1px solid var(--border)', width: FILTER_TREE_WIDTH }}
    >
      <div className="flex items-center justify-between px-1 py-1">
        <button
          onClick={onClearAll}
          className="text-xs font-[var(--font-mono)] tracking-wide uppercase transition-colors hover:text-[var(--accent)]"
          style={{ color: 'var(--text-muted)' }}
        >
          {t('canvas.clearAll')}
        </button>
      </div>
      {categories.map((category) => (
        <TreeNodeRow
          key={category.id}
          category={category}
          isExpanded={expandedNodes.has(category.id)}
          onToggleExpand={handleToggleExpand}
          selectedEntryIds={selectedEntryIds}
          onToggleEntry={handleToggleEntry}
        />
      ))}
    </div>
  )
}
